import NotificationsIcon from '@mui/icons-material/Notifications';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { endOfDay } from 'date-fns/endOfDay';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Money from '@mui/icons-material/AttachMoney';
import MoneyOff from '@mui/icons-material/MoneyOff';
import orderApi from '../../../api/order';
import { formatPaymentType, PaymentType, PaymentTypeStr } from '../../../common/enums';
import { Order, OrderHistoryItem } from '../../../common/types';
import InfoCard from '../../../components/card/InfoCard';
import ConfirmModal from '../../../components/modal/ConfirmModal';
import usePOSOrders from '../../../datastore/usePOSOrders';
import useAlert from '../../../hooks/useAlert';
import { getOrderBackgroundColours } from '../functions/orderFunctions';
import { formatCurrency } from '../../../common/moneyFunctions';
import PaymentModal from './components/PaymentModal';
import PosHeader from './components/PosHeader';
import ReceiptModal from './components/ReceiptModal';

export default function PosPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const walkUpOrder: Order | null = location.state?.order ?? null;
  const [paymentOrderId, setPaymentOrderId] = useState<number | null>(
    walkUpOrder?.id ? walkUpOrder.id : null,
  );
  const theme = useTheme();
  const {
    orders,
    count,
    params: getOrdersParams,
    updateParams,
    fetchOrders,
    startPolling,
    stopPolling,
    resetFilters,
  } = usePOSOrders();

  const [searchCustomer, setSearchCustomer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [removeOrderId, setRemoveOrderId] = useState<number | null>(null);
  const [invoiceOrderId, setInvoiceOrderId] = useState<number | null>(null);
  const { showAlert } = useAlert();

  const debounceNameSearchRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    startPolling({ initialInterval: 5000, maxInterval: 15000 })
    return () => stopPolling()
  }, [])

  useEffect(() => {
    if (debounceNameSearchRef.current) {
      clearTimeout(debounceNameSearchRef.current);
      debounceNameSearchRef.current = null;
    }

    debounceNameSearchRef.current = setTimeout(() => {
      updateParams({ customerName: searchCustomer });
      debounceNameSearchRef.current = null;
    }, 300);

    return () => {
      if (debounceNameSearchRef.current) {
        clearTimeout(debounceNameSearchRef.current);
        debounceNameSearchRef.current = null;
      }
    }
  }, [searchCustomer])

  const invoiceOrder = orders?.find((order) => order.orderId === invoiceOrderId);

  const walkUpReturnRoute: string = location.state?.returnRoute ?? '';

  async function handleMarkAsPaid(orderId: number, paymentType: PaymentTypeStr) {
    if (!orderId) {
      showAlert('Failed to mark order as paid.', 'error');
      return;
    }
    const order = orders.find((order) => order.orderId === orderId);
    if (!order) {
      showAlert('Failed to mark order as paid.', 'error');
      return;
    }
    try {
      setIsLoading(true);
      const result = await orderApi.markAsPaid(order.orderId, paymentType)

      if (result.status !== 200) {
        showAlert('Failed to mark order as paid.', 'error');
        return;
      }
      fetchOrders(getOrdersParams);
      showAlert('Order marked as paid.', 'success');
      if (walkUpReturnRoute) {
        navigate(walkUpReturnRoute);
      }
    } catch (err) {
      console.error(err);
      showAlert('Failed to mark order as paid.', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const handleStartDateChange = (date: Date | null) => {
    updateParams({
      startDate: date || startOfDay(new Date())
    })
  };

  const handleEndDateChange = (date: Date | null) => {
    updateParams({
      endDate: date || endOfDay(new Date())
    })
  };

  const handleResetFilters = () => {
    setSearchCustomer('');
    resetFilters();
  }

  async function handleRemove(orderId: number) {
    if (!orderId) {
      showAlert('Failed to remove order.', 'error');
      return;
    }

    try {
      setIsLoading(true);
      const result = await orderApi.removeOrder(orderId)
      if (result.status !== 200) {
        console.error('Failed to remove order, server error');
      } else {
        fetchOrders(getOrdersParams, true);
        showAlert('Order removed.', 'success');
      }
    } catch (err) {
      console.error(err);
      showAlert('Failed to remove order, refresh page and try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  function handlePageChange(_: React.ChangeEvent<unknown>, newPageNumber: number) {
    updateParams({ skip: (newPageNumber - 1) * getOrdersParams.take });
  }

  const paymentOrder = orders?.find((f) => f.orderId === paymentOrderId);
  const removeOrder = orders?.find((f) => f.orderId === removeOrderId);

  const totalPages = Math.ceil(count / getOrdersParams.take);
  const currentPage = Math.floor(getOrdersParams.skip / getOrdersParams.take) + 1;

  return (
    <>
      <Stack sx={{ height: '100vh', flexDirection: 'column' }}>
        <Box sx={{ p: 2, backgroundColor: theme.palette.common.grey }}>
          <PosHeader
            searchCustomer={searchCustomer}
            setSearchCustomer={setSearchCustomer}
            hidePaid={getOrdersParams.hidePaid}
            setHidePaid={(hidePaid) => updateParams({ hidePaid })}
            startDate={getOrdersParams.startDate}
            endDate={getOrdersParams.endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            resetFilters={handleResetFilters}
          />
        </Box>
        <Divider />

        <Stack
          sx={{ flexGrow: 1, overflow: 'hidden', flexDirection: 'column' }}
        >
          {orders.length === 0 ? (
            <InfoCard
              Icon={NotificationsIcon}
              title="No Results Found"
              subtitle="Try refreshing your page or check back later."
            />
          ) : (
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              <TableContainer>
                <Table stickyHeader sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">Customer Name</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">Created Time</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">Start Time</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">Finish Time</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">Payment</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">Total Price</Typography>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders?.map((order: OrderHistoryItem) => {
                      const orderTotal = order.totalPlusSurcharge ?? order.total
                      const { bodyBg } = getOrderBackgroundColours(order.isDraft)
                      return (
                        <TableRow
                          key={order.orderId}
                          onClick={() => navigate(`/staff/pos/${order.orderId}`)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: bodyBg,
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          }}
                        >
                          <TableCell width="20%">{order.customerName}</TableCell>
                          <TableCell width="10%">
                            <Typography variant="caption">
                              {order.createdAt
                                ? format(
                                  new Date(order.createdAt),
                                  'dd/MM/yy HH:mm a',
                                )
                                : ''}
                            </Typography>
                          </TableCell>
                          <TableCell width="10%">
                            <Typography variant="caption">
                              {order.startTime
                                ? format(
                                  new Date(order.startTime),
                                  'dd/MM/yy HH:mm a',
                                )
                                : ''}
                            </Typography>
                          </TableCell>
                          <TableCell width="10%">
                            <Typography variant="caption">
                              {order.finishTime
                                ? format(
                                  new Date(order.finishTime),
                                  'dd/MM/yy HH:mm a',
                                )
                                : ''}
                            </Typography>
                          </TableCell>
                          <TableCell width="15%">
                            {order.orderPayments.map((op) => (
                              <>
                                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'flex-start', width: '100%', color: op.paymentType === PaymentType.Defer ? theme.palette.error.main : theme.palette.primary.main }}>
                                  <Box>
                                    {op.paymentType === PaymentType.Defer ? 'Outstanding' : formatPaymentType(op.paymentType)}
                                  </Box>
                                  <Box sx={{ marginLeft: 'auto' }}>
                                    {formatCurrency(op.amount, true)}
                                  </Box>
                                </Stack>
                              </>
                            ))}
                          </TableCell>
                          <TableCell width="10%">
                            <Stack
                              direction="row"
                              sx={{
                                alignItems: 'center',
                                gap: 1,
                                color: !order.isPaid ? theme.palette.error.main : theme.palette.primary.main,
                                fontWeight: !order.isPaid ? 'bold' : 'normal',
                              }}
                              >
                              {order.isPaid ? <Money /> : <MoneyOff />}
                              ${orderTotal.toFixed(2)}
                            </Stack>
                          </TableCell>
                          <TableCell width="10%">
                            {
                              order.isPaid ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{ margin: 0, whiteSpace: 'nowrap' }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setInvoiceOrderId(order.orderId)
                                  }}
                                >
                                  Send Invoice
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{ margin: 0, whiteSpace: 'nowrap' }}
                                  disabled={isLoading}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsLoading(true);
                                    setPaymentOrderId(order.orderId);
                                  }}
                                >
                                  Mark as Paid
                                </Button>
                              )
                            }
                          </TableCell>
                          <TableCell width="10%">
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              sx={{ margin: 0, whiteSpace: 'nowrap' }}
                              disabled={isLoading}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsLoading(true);
                                setRemoveOrderId(order.orderId);
                              }}
                            >
                              Remove Order
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack spacing={2} direction="row" justifyContent="center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Stack>
          </Box>
        </Stack>
      </Stack>

      {removeOrderId && (
        <ConfirmModal
          onClose={() => {
            setIsLoading(false);
            setRemoveOrderId(null);
          }}
          title={`Remove order for ${removeOrder?.customerName?.toUpperCase()}`}
          content="Are you sure you want to remove this item?"
          onConfirm={() => {
            handleRemove(removeOrderId);
            setRemoveOrderId(null);
            setIsLoading(false);
          }}
        />
      )}
      {paymentOrderId && (
        <PaymentModal
          onClose={() => {
            setIsLoading(false);
            setPaymentOrderId(null);
          }}
          open={!!paymentOrderId}
          title={`Payment Method for ${paymentOrder?.customerName?.toUpperCase()}`}
          content="Please select the payment method for this order."
          onConfirm={(paymentType: PaymentTypeStr) => {
            handleMarkAsPaid(paymentOrderId, paymentType);
            setPaymentOrderId(null);
            setIsLoading(false);
          }}
        />
      )}
      {invoiceOrder && (
        <ReceiptModal
          onClose={() => {
            setInvoiceOrderId(null);
          }}
          orderId={invoiceOrder.orderId}
        />
      )}

    </>
  );
}
